import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  PieChart,
  BarChart3,
  ArrowUpRight,
  ArrowDownLeft,
  Plus,
  Search,
  Filter,
  Star,
  Clock,
  Shield,
  ChevronRight,
  Activity,
  Loader2,
  AlertCircle
} from 'lucide-react'
import { InvestmentsAPI, extractArray } from '../services/api'
import InvestmentModal from '../components/InvestmentModal'
import TradingChart from '../components/TradingChart'

// Market insights will use translation keys
const getMarketInsights = (t) => [
  { title: t('analysis.techRally'), description: t('analysis.techRallyDesc') },
  { title: t('analysis.fedUpdate'), description: t('analysis.fedUpdateDesc') },
  { title: t('analysis.cryptoVolatility'), description: t('analysis.cryptoVolatilityDesc') }
]

export default function InvestmentPage() {
  const { t, i18n } = useTranslation(['investments', 'common'])
  const [activeTab, setActiveTab] = useState('portfolio')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')

  // Get translated market insights
  const marketInsights = getMarketInsights(t)

  // Data states
  const [portfolio, setPortfolio] = useState([])
  const [availableInvestments, setAvailableInvestments] = useState([])
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Modal state
  const [investModalOpen, setInvestModalOpen] = useState(false)
  const [selectedSecurity, setSelectedSecurity] = useState(null)

  // Chart state
  const [chartSecurity, setChartSecurity] = useState(null)

  // Detail view state
  const [selectedHolding, setSelectedHolding] = useState(null)

  useEffect(() => {
    fetchData()
  }, [])

  // Auto-refresh prices every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!investModalOpen) {
        fetchSecuritiesOnly()
        // También actualizar portfolio para reflejar cambios en holdings
        fetchPortfolioOnly()
      }
    }, 5000)

    return () => clearInterval(interval)
  }, [investModalOpen]) // Removido chartSecurity para evitar recrear el intervalo

  // Fetch only securities for price updates (lighter call)
  const fetchSecuritiesOnly = async () => {
    try {
      const securitiesRes = await InvestmentsAPI.getSecurities()
      if (securitiesRes.success) {
        const securities = extractArray(securitiesRes)
        const mapped = securities.map(s => ({
          id: s.id,
          symbol: s.symbol,
          name: s.name,
          price: parseFloat(s.current_price) || 0,
          change: parseFloat(s.change_amount) || parseFloat(s.price_change) || 0,
          changePercent: parseFloat(s.change_percent) || parseFloat(s.price_change_percent) || 0,
          type: s.security_type || s.type || 'Stock',
          riskLevel: s.risk_level || 'Medium',
          description: s.description || '',
          current_price: s.current_price,
          change_percent: s.change_percent || s.price_change_percent,
          change_amount: s.change_amount || s.price_change,
          risk_level: s.risk_level,
          security_type: s.security_type || s.type,
          market_cap: s.market_cap
        }))
        setAvailableInvestments(mapped)

        // Update chart security with new price data (using ref to avoid stale closure)
        setChartSecurity(prev => {
          if (prev) {
            const updated = mapped.find(s => s.id === prev.id)
            return updated || prev
          }
          return prev
        })

        // Also update selectedHolding if it exists
        setSelectedHolding(prev => {
          if (prev) {
            const updated = mapped.find(s => s.symbol === prev.symbol)
            if (updated) {
              return { ...prev, ...updated }
            }
          }
          return prev
        })
      }
    } catch (err) {
      console.error('Error refreshing securities:', err)
    }
  }

  // Fetch only portfolio for price updates
  const fetchPortfolioOnly = async () => {
    try {
      const portfolioRes = await InvestmentsAPI.getPortfolio()
      if (portfolioRes.success) {
        const holdings = extractArray(portfolioRes, 'holdings')
        setPortfolio(holdings.map(h => ({
          id: h.id,
          symbol: h.symbol,
          name: h.name,
          shares: parseFloat(h.quantity) || 0,
          currentPrice: parseFloat(h.current_price) || 0,
          purchasePrice: parseFloat(h.average_cost) || 0,
          change: parseFloat(h.unrealized_gain_loss) || 0,
          changePercent: parseFloat(h.price_change_percent) || 0,
          value: parseFloat(h.current_value) || 0
        })))
      }
    } catch (err) {
      console.error('Error refreshing portfolio:', err)
    }
  }

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      console.log('Fetching investment data...')

      // Fetch portfolio data
      const [portfolioRes, securitiesRes, accountsRes] = await Promise.all([
        InvestmentsAPI.getPortfolio(),
        InvestmentsAPI.getSecurities(),
        InvestmentsAPI.getAccounts()
      ])

      console.log('Portfolio response:', portfolioRes)
      console.log('Securities response:', securitiesRes)
      console.log('Accounts response:', accountsRes)

      if (portfolioRes.success) {
        const holdings = extractArray(portfolioRes, 'holdings')
        // Map holdings to portfolio format
        setPortfolio(holdings.map(h => ({
          id: h.id,
          symbol: h.symbol,
          name: h.name,
          shares: parseFloat(h.quantity) || 0,
          currentPrice: parseFloat(h.current_price) || 0,
          purchasePrice: parseFloat(h.average_cost) || 0,
          change: parseFloat(h.unrealized_gain_loss) || 0,
          changePercent: parseFloat(h.price_change_percent) || 0,
          value: parseFloat(h.current_value) || 0
        })))
      }

      if (securitiesRes.success) {
        const securities = extractArray(securitiesRes)
        // Keep original API response format for modal compatibility
        setAvailableInvestments(securities.map(s => ({
          // Display fields (transformed)
          id: s.id,
          symbol: s.symbol,
          name: s.name,
          price: parseFloat(s.current_price) || 0,
          change: parseFloat(s.change_amount) || parseFloat(s.price_change) || 0,
          changePercent: parseFloat(s.change_percent) || parseFloat(s.price_change_percent) || 0,
          type: s.security_type || s.type || 'Stock',
          riskLevel: s.risk_level || 'Medium',
          description: s.description || '',
          // Original fields for modal
          current_price: s.current_price,
          change_percent: s.change_percent || s.price_change_percent,
          change_amount: s.change_amount || s.price_change,
          risk_level: s.risk_level,
          security_type: s.security_type || s.type
        })))
      }

      if (accountsRes.success) {
        setAccounts(extractArray(accountsRes))
      }

    } catch (err) {
      console.error('Investment fetch error:', err)
      setError(t('errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const portfolioValue = portfolio.reduce((total, stock) => total + stock.value, 0)
  const totalGainLoss = portfolio.reduce((total, stock) => total + stock.change, 0)
  const totalGainLossPercent = portfolioValue > 0 ? (totalGainLoss / (portfolioValue - totalGainLoss)) * 100 : 0

  const bestPerformer = portfolio.length > 0
    ? portfolio.reduce((best, stock) => stock.changePercent > best.changePercent ? stock : best, portfolio[0])
    : null

  const getRiskColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'low': return 'text-emerald-600 bg-emerald-50'
      case 'medium': return 'text-yellow-600 bg-yellow-50'
      case 'high': return 'text-orange-600 bg-orange-50'
      case 'very high': return 'text-red-600 bg-red-50'
      default: return 'text-slate-600 bg-slate-100'
    }
  }

  const filteredInvestments = availableInvestments.filter(inv => {
    const matchesSearch = inv.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         inv.symbol.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filterType === 'all' || inv.type?.toLowerCase() === filterType.toLowerCase()
    return matchesSearch && matchesFilter
  })

  const handleInvest = (security) => {
    setSelectedSecurity(security)
    setInvestModalOpen(true)
  }

  const handleInvestSuccess = () => {
    setInvestModalOpen(false)
    setSelectedSecurity(null)
    fetchData() // Refresh data after successful investment
  }

  // Handle viewing holding details (toggle behavior)
  const handleViewHolding = (holding) => {
    // Si ya está seleccionado el mismo holding, lo cerramos (toggle)
    if (selectedHolding?.symbol === holding.symbol) {
      setSelectedHolding(null)
      return
    }

    // Find the matching security from availableInvestments
    const security = availableInvestments.find(s => s.symbol === holding.symbol)
    if (security) {
      setSelectedHolding({
        ...holding,
        ...security
      })
    } else {
      setSelectedHolding(holding)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-slate-900">{t('title')}</h1>
                {activeTab === 'markets' && (
                  <span className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full text-xs text-emerald-700">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                    {t('chart.live')}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{t('subtitle')}</p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={fetchData}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-slate-300 rounded-lg transition-all text-sm font-medium text-slate-700"
              >
                <Activity className="w-4 h-4" />
                {t('common:refresh')}
              </button>
              <button
                onClick={() => { setSelectedSecurity(null); setInvestModalOpen(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                {t('trade.invest')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 max-w-[1600px] mx-auto">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Portfolio Summary */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">{t('portfolio.totalValue')}</p>
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <PieChart className="w-4 h-4 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">${portfolioValue.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}</p>
            <p className="text-xs text-slate-500 mt-1">{t('portfolio.holdings')}: {portfolio.length}</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">{t('portfolio.totalGainLoss')}</p>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${totalGainLoss >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                {totalGainLoss >= 0 ? <TrendingUp className="w-4 h-4 text-emerald-600" /> : <TrendingDown className="w-4 h-4 text-red-600" />}
              </div>
            </div>
            <p className={`text-2xl font-bold ${totalGainLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {totalGainLoss >= 0 ? '+' : ''}${totalGainLoss.toFixed(2)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {totalGainLossPercent >= 0 ? '+' : ''}{totalGainLossPercent.toFixed(2)}%
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">{t('markets.topGainers')}</p>
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Star className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">{bestPerformer?.symbol || '-'}</p>
            <p className={`text-xs mt-1 ${bestPerformer?.changePercent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {bestPerformer ? `${bestPerformer.changePercent >= 0 ? '+' : ''}${bestPerformer.changePercent.toFixed(2)}%` : '-'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 bg-slate-100 rounded-lg p-1 mb-8">
          {[
            { key: 'portfolio', label: t('tabs.portfolio') },
            { key: 'markets', label: t('tabs.markets') },
            { key: 'research', label: t('tabs.analysis') }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-6 py-2.5 rounded-lg transition-colors text-sm font-medium ${
                activeTab === tab.key
                  ? 'bg-white shadow-sm text-slate-900'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Portfolio Tab */}
        {activeTab === 'portfolio' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-4">{t('portfolio.title')}</h3>
              {portfolio.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <PieChart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('portfolio.noHoldings')}</h3>
                  <p className="text-slate-500 mb-4">{t('portfolio.startInvesting')}</p>
                  <button
                    onClick={() => setActiveTab('markets')}
                    className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
                  >
                    {t('portfolio.exploreMarkets')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {portfolio.map((stock, index) => (
                    <motion.div
                      key={stock.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`bg-white border rounded-lg p-4 transition-colors cursor-pointer ${
                        selectedHolding?.symbol === stock.symbol
                          ? 'border-blue-500 ring-1 ring-blue-500'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleViewHolding(stock)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                            <span className="text-slate-900 font-bold text-sm">{stock.symbol?.charAt(0)}</span>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-slate-900">{stock.symbol}</h4>
                            <p className="text-xs text-slate-500">{stock.name}</p>
                            <p className="text-xs text-slate-500">{stock.shares} {t('trade.shares')}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-8">
                          <div className="text-right">
                            <p className="text-sm font-bold text-slate-900">${stock.value.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}</p>
                            <p className="text-xs text-slate-500">${stock.currentPrice.toFixed(2)}</p>
                          </div>

                          <div className="text-right">
                            <div className={`flex items-center justify-end text-xs ${stock.change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {stock.change >= 0 ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownLeft className="w-3 h-3 mr-1" />}
                              {stock.change >= 0 ? '+' : ''}${Math.abs(stock.change).toFixed(2)} ({stock.changePercent.toFixed(2)}%)
                            </div>
                          </div>

                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewHolding(stock); }}
                            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                            title={t('common:viewDetails')}
                          >
                            <ChevronRight className={`w-4 h-4 transition-transform ${selectedHolding?.symbol === stock.symbol ? 'rotate-90 text-blue-500' : 'text-slate-400'}`} />
                          </button>
                        </div>
                      </div>

                      {/* Expanded Detail Panel */}
                      {selectedHolding?.symbol === stock.symbol && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 pt-4 border-t border-slate-200"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Chart */}
                          <div className="mb-4">
                            <TradingChart security={selectedHolding} />
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-slate-500 mb-1">{t('holdings.currentPrice')}</p>
                              <p className="text-sm font-bold text-slate-900">${stock.currentPrice.toFixed(2)}</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-slate-500 mb-1">{t('holdings.purchasePrice')}</p>
                              <p className="text-sm font-bold text-slate-900">${stock.purchasePrice.toFixed(2)}</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-slate-500 mb-1">{t('holdings.quantity')}</p>
                              <p className="text-sm font-bold text-slate-900">{stock.shares} {t('trade.shares')}</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-slate-500 mb-1">{t('holdings.totalValue')}</p>
                              <p className="text-sm font-bold text-slate-900">${stock.value.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}</p>
                            </div>
                          </div>

                          {/* Performance */}
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className={`rounded-lg p-3 ${stock.change >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                              <p className="text-xs text-slate-500 mb-1">{t('holdings.gainLoss')}</p>
                              <p className={`text-sm font-bold ${stock.change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {stock.change >= 0 ? '+' : ''}${stock.change.toFixed(2)}
                              </p>
                            </div>
                            <div className={`rounded-lg p-3 ${stock.changePercent >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                              <p className="text-xs text-slate-500 mb-1">{t('security.changePercent')}</p>
                              <p className={`text-sm font-bold ${stock.changePercent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {stock.changePercent >= 0 ? '+' : ''}{stock.changePercent.toFixed(2)}%
                              </p>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleInvest(selectedHolding); }}
                              className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
                            >
                              <Plus className="w-4 h-4" />
                              {t('trade.buyMore')}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setSelectedHolding(null); }}
                              className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm font-medium"
                            >
                              {t('common:close')}
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Markets Tab */}
        {activeTab === 'markets' && (
          <div className="space-y-6">
            {/* Trading Chart */}
            {chartSecurity && (
              <TradingChart
                security={chartSecurity}
                onInvest={handleInvest}
              />
            )}

            {/* Search and Filters */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={t('markets.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-900 transition-colors"
                />
              </div>

              <div className="flex gap-2">
                {[
                  { key: 'all', label: t('markets.filters.all') },
                  { key: 'stock', label: t('markets.filters.stocks') },
                  { key: 'etf', label: t('markets.filters.etfs') },
                  { key: 'crypto', label: t('markets.filters.crypto') }
                ].map((type) => (
                  <button
                    key={type.key}
                    onClick={() => setFilterType(type.key)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                      filterType === type.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Available Investments */}
            {filteredInvestments.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{t('orders.noOrders')}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {filteredInvestments.map((investment, index) => (
                  <motion.div
                    key={investment.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <span className="text-slate-900 font-bold text-sm">{investment.symbol?.charAt(0)}</span>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">{investment.symbol}</h4>
                          <p className="text-xs text-slate-500">{investment.name}</p>
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-900">${investment.price.toLocaleString()}</p>
                        <div className={`flex items-center text-xs ${investment.change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {investment.change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}
                          {investment.change >= 0 ? '+' : ''}${Math.abs(investment.change).toFixed(2)} ({investment.changePercent.toFixed(2)}%)
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium capitalize">{investment.type}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getRiskColor(investment.riskLevel)}`}>
                        {investment.riskLevel}
                      </span>
                    </div>

                    {investment.description && (
                      <p className="text-xs text-slate-600 mb-3">{investment.description}</p>
                    )}

                    <div className="flex gap-2">
                      <button
                        onClick={() => setChartSecurity(chartSecurity?.id === investment.id ? null : investment)}
                        className={`flex-1 py-2 rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-1.5 ${
                          chartSecurity?.id === investment.id
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        <BarChart3 className="w-4 h-4" />
                        {t('common:chart')}
                      </button>
                      <button
                        onClick={() => handleInvest(investment)}
                        className="flex-1 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-colors text-sm font-medium"
                      >
                        {t('trade.invest')}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Research Tab */}
        {activeTab === 'research' && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                </div>
                <h4 className="text-sm font-semibold text-slate-900">{t('analysis.insights')}</h4>
              </div>
              <div className="space-y-3">
                {marketInsights.map((insight, index) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-lg">
                    <h5 className="text-sm font-semibold text-slate-900 mb-1">{insight.title}</h5>
                    <p className="text-xs text-slate-600">{insight.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-emerald-600" />
                </div>
                <h4 className="text-sm font-semibold text-slate-900">{t('analysis.tips')}</h4>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">1</span>
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-slate-900">{t('analysis.diversify')}</h5>
                    <p className="text-xs text-slate-600">{t('analysis.diversifyDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">2</span>
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-slate-900">{t('analysis.dollarCostAvg')}</h5>
                    <p className="text-xs text-slate-600">{t('analysis.dollarCostAvgDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">3</span>
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-slate-900">{t('analysis.longTerm')}</h5>
                    <p className="text-xs text-slate-600">{t('analysis.longTermDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Investment Modal */}
      <InvestmentModal
        isOpen={investModalOpen}
        onClose={() => { setInvestModalOpen(false); setSelectedSecurity(null); }}
        security={selectedSecurity}
        securities={availableInvestments}
        onSuccess={handleInvestSuccess}
      />
    </div>
  )
}
